import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const navLinks = location.pathname === '/' ? [
    { label: 'About Us', action: () => scrollToSection('about') },
    { label: 'Services', action: () => scrollToSection('services') },
    { label: 'Team', action: () => scrollToSection('team') },
    { label: 'Contact', action: () => scrollToSection('contact') }
  ] : [];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-[#0878BF] ${
      isScrolled ? 'bg-[#0878BF] shadow-md' : 'bg-[#0878BF] backdrop-blur-sm'
    }`}>
      <div className="container mx-auto px-0">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src="/FMALSimpleLogoHD.jpg" 
              alt="Find Me A Lender" 
              className="h-12 w-auto"
            />
            
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-5">
            {/* Separator */}
            <div className="h-6 w-px"></div>
            <Link to="/" className="text-[#F7F9FF] hover:text-[#6793D1] transition-colors font-medium">
              Home
            </Link>
            {location.pathname === '/' && navLinks.map((link, index) => (
              <button
                key={index}
                onClick={link.action}
                className="text-[#F7F9FF] hover:text-[#6793D1] transition-colors font-medium"
              >
                {link.label}
              </button>
            ))}
            
            {/* Separator */}
            <div className="h-6 w-px bg-gray-200"></div>
            
            <Link to="/learn-more" className="text-[#F7F9FF] hover:text-[#6793D1] transition-colors font-medium 10-px">
              Resources
            </Link>
            
            {/* Separator */}
            <div className="h-6 w-px bg-gray-300"></div>
            
            <Link to="/career" className="text-[#F7F9FF] hover:text-[#6793D1] transition-colors font-medium">
              Career
            </Link>

            {/* Separator */}
            <div className="h-6 w-px bg-gray-300"></div>

            <Link to="/community" className="text-[#F7F9FF] hover:text-[#6793D1] transition-colors font-medium">
              Awards
            </Link>

            {/* Separator */}
            <div className="h-6 w-px bg-gray-300"></div>

            <Link to="/privacy" className="text-[#F7F9FF] hover:text-[#6793D1] transition-colors font-medium">
              Privacy &amp; Credit Guide
            </Link>

            {/* Separator */}
            <div className="h-6 w-px"></div>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <a href="tel:1300078494" className="flex items-center space-x-2 text-[#F7F9FF] hover:text-[#6793D1] transition-colors">
              <Phone className="h-4 w-4" />
              <span className="text-sm font-medium">1300 078 494</span>
            </a>
            <Button 
              onClick={() => scrollToSection('contact')}
              style={{ backgroundColor: '#F7F9FF' }}
              className="text-[#0878BF] hover:opacity-90 transition-opacity"
            >
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-[#F7F9FF]"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#0878BF] border-t">
          <nav className="container mx-auto px-4 py-4 flex flex-col space-y-4">
            <Link 
              to="/" 
              className="text-[#F7F9FF] hover:text-[#6793D1] transition-colors font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>
            {location.pathname === '/' && navLinks.map((link, index) => (
              <button
                key={index}
                onClick={link.action}
                className="text-left text-[#F7F9FF] hover:text-[#6793D1] transition-colors font-medium py-2"
              >
                {link.label}
              </button>
            ))}
            <Link 
              to="/learn-more" 
              className="text-[#F7F9FF] hover:text-[#6793D1] transition-colors font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Resources
            </Link>
            <Link 
              to="/career" 
              className="text-[#F7F9FF] hover:text-[#6793D1] transition-colors font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Career
            </Link>
            <Link 
              to="/community" 
              className="text-[#F7F9FF] hover:text-[#6793D1] transition-colors font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Community   
            </Link>
            <Link 
              to="/privacy" 
              className="text-[#F7F9FF] hover:text-[#6793D1] transition-colors font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Privacy &amp; Credit Guide
            </Link>

            <a href="tel:1300078494" className="flex items-center space-x-2 text-[#F7F9FF] hover:text-[#6793D1] transition-colors py-2">
              <Phone className="h-4 w-4" />
              <span className="font-medium">1300 078 494</span>
            </a>
            <Button 
              onClick={() => scrollToSection('contact')}
              style={{ backgroundColor: '#F7F9FF' }}
              className="text-[#0878BF] w-full"
            >
              Get Started
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
